import type { HttpServerConfig, MCPServerConfig } from "@sphinx/sdk/browser";
import type { LoggingLevel } from "@modelcontextprotocol/client";
import { authFetch } from "@/lib/session-token";
import { HOSTED_MODE } from "@/lib/config";
import type { ConnectionDefaults } from "@/shared/connection-defaults";

/**
 * Extracts an OAuth access token from an HttpServerConfig's Authorization header.
 * Returns undefined if the config isn't an HTTP config or has no Bearer token.
 */
function extractOAuthToken(serverConfig: MCPServerConfig): string | undefined {
  const httpConfig = serverConfig as HttpServerConfig;
  const authHeader = (
    httpConfig?.requestInit?.headers as Record<string, string>
  )?.["Authorization"];
  if (authHeader && authHeader.startsWith("Bearer ")) {
    return authHeader.slice("Bearer ".length);
  }
  return undefined;
}

async function authFetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = 10000,
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await authFetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(
        `Connection attempt timed out after ${
          timeoutMs / 1000
        } seconds. The server may not exist or is not responding.`,
      );
    }
    throw error;
  }
}

function buildResolverBody(
  serverId: string,
  options: {
    projectId: string;
    serverName?: string;
    connectionDefaults?: ConnectionDefaults;
    oauthAccessToken?: string;
  },
): Record<string, unknown> {
  return {
    projectId: options.projectId,
    serverId,
    ...(options.serverName ? { serverName: options.serverName } : {}),
    ...(options.connectionDefaults
      ? { connectionDefaults: options.connectionDefaults }
      : {}),
    // The browser holds OAuth tokens; the server attaches this bearer to the
    // upstream MCP requests for this connection.
    ...(options.oauthAccessToken
      ? { oauthAccessToken: options.oauthAccessToken }
      : {}),
  };
}

export async function testConnection(
  serverConfig: MCPServerConfig,
  serverId: string,
  options?: {
    projectId?: string;
    serverName?: string;
    connectionDefaults?: ConnectionDefaults;
    oauthAccessToken?: string;
  },
) {
  if (!options?.projectId) {
    throw new Error(
      "projectId is required for testConnection in local mode (server must be synced to Convex first)",
    );
  }

  const body = buildResolverBody(serverId, {
    projectId: options.projectId,
    serverName: options.serverName,
    connectionDefaults: options.connectionDefaults,
    oauthAccessToken:
      options.oauthAccessToken ?? extractOAuthToken(serverConfig),
  });

  const res = await authFetchWithTimeout(
    "/api/mcp/connect",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    20000, // 20 second timeout
  );
  return res.json();
}

export async function deleteServer(serverId: string) {
  if (HOSTED_MODE) {
    void serverId;
    return { success: true };
  }

  const res = await authFetch(
    `/api/mcp/servers/${encodeURIComponent(serverId)}`,
    {
      method: "DELETE",
    },
  );
  return res.json();
}

export async function disconnectAllRuntimeServers() {
  if (HOSTED_MODE) {
    return { success: true, servers: [] };
  }

  const result = await listServers();
  if (!result?.success || !Array.isArray(result.servers)) {
    return result;
  }

  const listedServers = result.servers as Array<{
    id?: unknown;
    name?: unknown;
  }>;
  const serverIds: string[] = listedServers.reduce(
    (ids: string[], server) => {
      if (typeof server.id === "string" && server.id) {
        ids.push(server.id);
      } else if (typeof server.name === "string" && server.name) {
        ids.push(server.name);
      }
      return ids;
    },
    [] as string[],
  );

  const disconnectResults = await Promise.allSettled(
    serverIds.map((serverId) => deleteServer(serverId)),
  );
  const failures = disconnectResults.filter((disconnectResult) => {
    if (disconnectResult.status === "rejected") {
      return true;
    }
    return disconnectResult.value?.success === false;
  });

  return {
    success: failures.length === 0,
    servers: result.servers,
    ...(failures.length > 0
      ? { error: `Failed to disconnect ${failures.length} server(s)` }
      : {}),
  };
}

export async function listServers() {
  if (HOSTED_MODE) {
    return { success: true, servers: [] };
  }

  const res = await authFetch("/api/mcp/servers");
  return res.json();
}

export async function reconnectServer(
  serverId: string,
  serverConfig: MCPServerConfig,
  options?: {
    projectId?: string;
    serverName?: string;
    connectionDefaults?: ConnectionDefaults;
    oauthAccessToken?: string;
  },
) {
  if (!options?.projectId) {
    throw new Error(
      "projectId is required for reconnectServer in local mode (server must be synced to Convex first)",
    );
  }

  const body = buildResolverBody(serverId, {
    projectId: options.projectId,
    serverName: options.serverName,
    connectionDefaults: options.connectionDefaults,
    oauthAccessToken:
      options.oauthAccessToken ?? extractOAuthToken(serverConfig),
  });

  const res = await authFetchWithTimeout(
    "/api/mcp/servers/reconnect",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    20000, // 20 second timeout
  );
  return res.json();
}

export async function getInitializationInfo(serverId: string) {
  if (HOSTED_MODE) {
    // In hosted mode, init info is returned inline from /validate.
    // This fallback only runs if the validate response lacked initInfo.
    return { success: true, initInfo: null };
  }

  const res = await authFetch(
    `/api/mcp/servers/init-info/${encodeURIComponent(serverId)}`,
  );
  return res.json();
}

export async function setServerLoggingLevel(
  serverId: string,
  // `null` opts out of the modern per-request mechanism (absent `_meta` key
  // on the wire). Not meaningful for the legacy `logging/setLevel`
  // mechanism — the server route rejects it there.
  level: LoggingLevel | null,
) {
  if (HOSTED_MODE) {
    void serverId;
    void level;
    return {
      success: false,
      error: "Server logging level is not supported in hosted mode",
    };
  }

  const res = await authFetch("/api/mcp/log-level", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serverId, level }),
  });
  return res.json();
}
